import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { backendApi } from "@/api/backendApi";
import { 
  ArrowLeft, 
  MapPin, 
  Building2, 
  CheckCircle, 
  Loader2, 
  FileText,
  Send
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export default function ApplyPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [coverLetter, setCoverLetter] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  // Fetch job details
  const { data: job, isLoading: isJobLoading } = useQuery({
    queryKey: ["job", id],
    queryFn: () => backendApi.jobs.getById(id),
  });

  // Check if already applied
  const { data: applications } = useQuery({
    queryKey: ["my-applications"],
    queryFn: () => backendApi.applications.getMy(),
  });

  const alreadyApplied = applications?.some(a => (a.job_id || a.job?.id) === parseInt(id));

  const applyMutation = useMutation({
    mutationFn: (data) => backendApi.applications.apply(data),
    onSuccess: () => {
      setIsSuccess(true);
      queryClient.invalidateQueries(["my-applications"]);
      queryClient.invalidateQueries(["notifications"]);
    },
    onError: (error) => {
      alert(error.message || "Failed to submit application");
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    applyMutation.mutate({
      job_id: id,
      cover_letter: coverLetter,
      // resume_url would ideally come from the user's profile
    });
  };

  if (isJobLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-10 h-10 text-[#2557a7] animate-spin" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-4">
        <h2 className="text-2xl font-bold text-slate-800 mb-2">Job not found</h2>
        <Button onClick={() => navigate("/jobs/list")}>Back to Jobs</Button>
      </div>
    );
  }

  if (isSuccess || alreadyApplied) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-xl p-10 text-center border border-slate-100">
          <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">
            {alreadyApplied ? "Already Applied" : "Application Sent!"}
          </h2>
          <p className="text-slate-500 mb-8 leading-relaxed">
            {alreadyApplied 
              ? "You have already submitted an application for this position. You can track its status in your dashboard."
              : "Your application for " + job.title + " has been successfully submitted to " + job.company + "."}
          </p>
          <div className="flex flex-col gap-3">
            <Button 
              className="bg-[#2557a7] hover:bg-[#1e4687] text-white h-12 rounded-xl font-bold"
              onClick={() => navigate("/jobs/dashboard")}
            >
              Go to Dashboard
            </Button>
            <Button variant="ghost" className="text-slate-500" onClick={() => navigate("/jobs/list")}>
              Find more jobs
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-20">
      <div className="max-w-3xl mx-auto px-4 pt-12">
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center text-sm font-semibold text-slate-500 hover:text-[#2557a7] mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to job details
        </button>

        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden mb-8">
          <div className="p-8 border-b border-slate-50 bg-slate-50/50">
            <div className="flex gap-5 items-start">
              <div className="w-14 h-14 rounded-2xl bg-white border border-slate-100 flex items-center justify-center shrink-0">
                <Building2 className="w-7 h-7 text-slate-300" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 leading-tight">Apply for {job.title}</h1>
                <p className="text-[#2557a7] font-semibold mt-1">{job.company}</p>
                <div className="flex items-center gap-3 text-sm text-slate-500 mt-2">
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" />
                    {job.location}
                  </span>
                  <span>•</span>
                  <span>{job.work_mode || "Full-time"}</span>
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-8 space-y-8">
            {/* Resume Selection Info */}
            <div className="p-5 bg-blue-50/50 rounded-2xl border border-blue-100 flex gap-4">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5 text-[#2557a7]" />
              </div>
              <div>
                <h4 className="font-bold text-slate-900 text-sm">Resume on file</h4>
                <p className="text-xs text-slate-500 mt-0.5">Your current resume from your profile will be sent to the recruiter.</p>
                <Link to="/jobs/profile" className="text-xs font-bold text-[#2557a7] mt-2 inline-block hover:underline">
                  Update Resume
                </Link>
              </div>
            </div>

            {/* Cover Letter */}
            <div>
              <label className="text-sm font-bold text-slate-700 mb-3 block">
                Cover Letter (Optional)
              </label>
              <Textarea 
                value={coverLetter}
                onChange={(e) => setCoverLetter(e.target.value)}
                placeholder="Why are you a good fit for this role?"
                className="min-h-[200px] rounded-2xl border-slate-200 focus:ring-[#2557a7] focus:border-[#2557a7] p-4 text-base"
              />
              <p className="text-xs text-slate-400 mt-3">
                Briefly describe your experience and interest in this role.
              </p>
            </div>

            <div className="pt-4 flex gap-4">
              <Button 
                type="submit"
                disabled={applyMutation.isPending}
                className="flex-1 bg-[#2557a7] hover:bg-[#1e4687] text-white h-14 rounded-2xl font-bold text-lg shadow-xl shadow-blue-100 disabled:opacity-70 transition-all hover:scale-[1.02]"
              >
                {applyMutation.isPending ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit Application
                    <Send className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                className="h-14 rounded-2xl px-8 border-slate-200 text-slate-500 font-semibold"
                onClick={() => navigate(-1)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
        
        <p className="text-center text-xs text-slate-400">
          By submitting your application, you agree to WebIdeal's <Link to="/terms" className="underline">Terms of Service</Link> and <Link to="/privacy" className="underline">Privacy Policy</Link>.
        </p>
      </div>
    </div>
  );
}
